<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">

          <BackButton
              :label="$t('general.button.back')"
              :to="{ name: 'coursePrivateCoachingList' }"
              marginBottom="5"/>

          <div class="d-flex justify-content-between">
            <h6 class="element-header">
              {{ $t('course.general_title') }}
            </h6>
          </div>

          <div class="element-box">
            <h5 class="form-header">
              {{ $t('course.private_coaching.title') }}
            </h5>

            <div v-if="isLoaded">
              <form @submit.prevent="submitForm" novalidate>
                <div class="row">
                  <div class="col-lg-6 col-md-6 col-sm-12">
                    <BaseInput
                        v-model="formData.nameOfCoaching"
                        label="Bezeichnung" :required=true
                        :validation="v$.nameOfCoaching"
                        :messages="v$.nameOfCoaching.required.$message">
                    </BaseInput>
                    <div v-if="privateCoachingOperationTypes && privateCoachingOperationTypes.length">
                      <SelectVue
                          v-model="formData.operationType"
                          :options="privateCoachingOperationTypes"
                          @update:modelValue="updateOperationType"
                          :is-clear="true"
                          :required="true"
                          label="Maßnahmetyp"
                          placeholder="Maßeinheitentyp auswählen"
                          :validation="v$.operationType"
                          :messages="v$.operationType.required.$message"/>
                    </div>
                    <BaseInput
                        v-model="formData.actionNumber"
                        label="Maßnahmennummer" :required=true
                        :validation="v$.actionNumber"
                        :messages="v$.actionNumber.required.$message">
                    </BaseInput>
                    <BaseNumber
                        v-model="formData.duration"
                        label="Dauer der Maßnahme (in Wochen)" :required=true
                        :validation="v$.duration"
                        :messages="v$.duration.required.$message">
                    </BaseNumber>
                    <div class="row">
                      <div class="col-lg-6">
                        <BaseNumber
                            v-model="formData.units"
                            label="Einheiten (Gesamtanzahl)"
                            :required=true
                            :validation="v$.units"
                            :messages="v$.units.required.$message">
                        </BaseNumber>
                      </div>
                      <div class="col-lg-6">
                        <BaseNumber
                            v-model="formData.cost"
                            :isCurrency=true
                            label="Umsatz pro Einheit"
                            :decimalPlaces="4"
                            :required=true
                            :validation="v$.cost"
                            :messages="v$.cost.required.$message">
                        </BaseNumber>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-lg-6">
                        <Datepicker
                            v-model="formData.startDate"
                            :required="true" label="Gültigkeitsstart"
                            :validation="v$.startDate"
                            :messages="v$.startDate.required.$message"/>
                      </div>
                      <div class="col-lg-6">
                        <Datepicker
                            v-model="formData.endDate"
                            :required="true" label="Gültigkeitsende"
                            :validation="v$.endDate"
                            :messages="v$.endDate.required.$message"/>
                      </div>
                    </div>
                  </div>
                  <div class="col-lg-6 col-md-6 col-sm-12">
                    <div v-if="officeDropdown && officeDropdown.length">
                      <SelectVue
                          v-model="formData.officeIds"
                          :options="officeDropdown"
                          :is-clear="true"
                          :is-multi="true"
                          :required="true"
                          label="Standort"
                          :validation="v$.officeIds"
                          :messages="v$.officeIds.required.$message"/>
                    </div>
                    <div v-if="clientDropdown && clientDropdown.length">
                      <SelectVue
                          v-model="formData.clientIds"
                          :options="clientDropdown"
                          :is-clear="true"
                          :is-multi="true"
                          :required="true"
                          :label="$t('placeholder.client_name')"
                          :validation="v$.clientIds"
                          :messages="v$.clientIds.required.$message"/>
                    </div>
                    <div v-if="moduleDropdown && moduleDropdown.length">
                      <SelectVue
                          v-model="formData.moduleIds"
                          :options="moduleDropdown"
                          :is-clear="true"
                          :is-multi="true"
                          :required="true"
                          label="Module"
                          :validation="v$.moduleIds"
                          :messages="v$.moduleIds?.required?.$message"/>
                    </div>
                  </div>

                </div>
                <div class="form-buttons-w text-center">
                  <SaveButton
                      :label="$t('general.button.save')"
                      :loading="isSubmitting"/>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Sidebar></Sidebar>
    </div>
  </div>
</template>

<script setup>
import coursePrivateCoachingService from '@/api/Course/PrivateCoaching';
import dropdownService from '@/api/Dropdown';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseNumber from '@/components/base/BaseNumber.vue';
import Sidebar from '@/components/FormSidebar.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import SelectVue from '@/components/SelectVue.vue';
import {ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {getClientDropdown, getOfficeDropdown} from '@/shared/dropdowns';
import {requiredRule} from '@/shared/validators/validationRules';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import useVuelidate from '@vuelidate/core';
import 'daterangepicker';
import 'daterangepicker/daterangepicker.css';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import BackButton from "@/components/button/BackButton.vue";
import SaveButton from "@/components/button/SaveButton.vue";
import {piniaCompanySettingsStore} from "@/store/piniaCompanySettingsStore";

const piniaCompanySettingsState = piniaCompanySettingsStore()
const companySettings = computed(() => piniaCompanySettingsState.companySettings)
const companyKey = companySettings.value.companyKey;

const {t} = useI18n();
const piniaEnumsState = piniaEnumsStore();
const {privateCoachingOperationTypes} = computed(() => piniaEnumsState.enums).value;
const officeDropdown = ref([]);
const clientDropdown = ref([]);
const moduleDropdown = ref([]);
const isSubmitting = ref(false);
const route = useRoute();
const courseId = route.params.id;
const isLoaded = ref(false);

const formData = ref({
  nameOfCoaching: '',
  operationType: '',
  actionNumber: '',
  duration: '',
  units: null,
  cost: null,
  startDate: '',
  endDate: '',
  officeIds: [],
  clientIds: [],
  moduleIds: [],
});

const rules = ref({
  nameOfCoaching: requiredRule(t),
  operationType: requiredRule(t),
  actionNumber: requiredRule(t),
  duration: requiredRule(t),
  units: requiredRule(t),
  cost: requiredRule(t),
  startDate: requiredRule(t),
  endDate: requiredRule(t),
  officeIds: requiredRule(t),
  clientIds: requiredRule(t),
  moduleIds: '',
});

const computedRules = computed(() => rules.value);

const v$ = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (courseId) {
      await coursePrivateCoachingService.putCourse(formData.value, +courseId);
    } else {
      await coursePrivateCoachingService.postCourse(formData.value);
    }

    await router.push({name: 'coursePrivateCoachingList'});

    isSubmitting.value = false;
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getCourse() {
  try {
    const {data} = await coursePrivateCoachingService.getCourse(+courseId);
    const locations = data.data.locations || [];
    const clients = data.data.clients || [];
    const modules = data.data?.modules || [];


    await updateOperationType(data.data.operationType);

    const officeArray = locations.map(location => location?.id).filter(id => id != null);
    const clientArray = clients.map(client => client?.id).filter(id => id != null);
    const moduleArray = modules.map(module => module?.id).filter(id => id != null);

    formData.value = {...data.data, officeIds: officeArray, clientIds: clientArray, moduleIds: moduleArray};
  } catch (error) {
    await ErrorToast(error);
  }
}

async function updateOperationType(operationType) {
  if (operationType === 'modular_integration_coaching' || operationType === 'go_work_mobile') {
    rules.value = {
      ...rules.value,
      moduleIds: requiredRule(t),
    };
    try {
      const {data} = await dropdownService.getCoursePrivateCoachingModule(operationType);
      moduleDropdown.value = data.data.map(item => ({
        label: `${item.name}`,
        value: item?.id,
      }));
    } catch (error) {
      await ErrorToast(error);
    }
  } else {
    formData.value.moduleIds = [];
    rules.value = {
      ...rules.value,
      moduleIds: '',
    };
    moduleDropdown.value = [];
  }
}

onMounted(async () => {
  if(companyKey === "BBQ"){
    router.push({ name: 'coursePrivateCoachingList' });
  }

  await Promise.all([
    getOfficeDropdown(officeDropdown),
    getClientDropdown(clientDropdown),
  ]);

  if (courseId) {
    await getCourse();
  }

  isLoaded.value = true;
});

</script>

<style scoped></style>